import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CountryFlagIcon } from "@/components/geo/CountryFlagIcon";
import type { CountryOption } from "@/lib/geo/geoStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

type ImportCountrySelectProps = {
  value: number | string | null;
  countries: CountryOption[];
  onChange: (countryId: number | null) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

export function ImportCountrySelect({
  value,
  countries,
  onChange,
  placeholder = "Select import country",
  triggerClassName,
  disabled = false,
}: ImportCountrySelectProps) {
  const [open, setOpen] = useState(false);

  const selectedCountry = countries.find(
    (c) => String(c.id) === String(value ?? "")
  );

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "glass-input h-11 w-full justify-between font-normal text-sm px-3 min-w-0",
            !selectedCountry && "text-muted-foreground",
            triggerClassName
          )}
        >
          <span className="truncate min-w-0">
            {selectedCountry ? (
              <span className="flex items-center gap-2 truncate">
                <CountryFlagIcon iso2={selectedCountry.iso2} countryName={selectedCountry.name} />
                <span className="truncate font-medium text-foreground">{selectedCountry.name}</span>
                {selectedCountry.currencyCode ? (
                  <span className="text-xs text-muted-foreground font-normal shrink-0">
                    ({selectedCountry.currencyCode})
                  </span>
                ) : null}
              </span>
            ) : (
              placeholder
            )}
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0 z-[100]" align="start" sideOffset={4}>
        <Command shouldFilter>
          <CommandInput placeholder="Search country, code, or currency..." className="h-9 text-xs" />
          <CommandList className="max-h-[260px] overflow-y-auto p-1">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No country found.
            </CommandEmpty>
            <CommandGroup>
              {countries.map((c) => {
                const isSelected = String(c.id) === String(value ?? "");
                const searchValue = `${c.name} ${c.iso2 ?? ""} ${c.iso3 ?? ""} ${c.currencyCode ?? ""}`;
                return (
                  <CommandItem
                    key={c.id}
                    value={searchValue}
                    onSelect={() => {
                      onChange(c.id);
                      setOpen(false);
                    }}
                    className="text-xs flex items-center justify-between cursor-pointer px-2.5 py-2 rounded-md my-0.5"
                  >
                    <span className="flex items-center gap-2 truncate min-w-0">
                      <CountryFlagIcon iso2={c.iso2} countryName={c.name} />
                      <span className="truncate">{c.name}</span>
                      {c.currencyCode ? (
                        <span className="text-xs text-muted-foreground font-normal shrink-0">
                          ({c.currencyCode})
                        </span>
                      ) : null}
                    </span>
                    <Check
                      className={cn(
                        "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
